/**
 * 要素手動追加メニュー
 * - テキスト追加
 * - 画像プレースホルダー追加
 * - ベクター shape 追加（5 カテゴリのグリッドから選択）
 *   - 基本図形 / 装飾・記号 / フレーム / スタンプ装飾 / 有機的・抽象
 *   - カスタム SVG（ユーザーアップロード）も同モーダル内に表示
 *   - モーダル下部から SVG アップロード可能
 *
 * 追加した要素はキャンバス中央付近に配置され、すぐ選択可能。
 */
import {
  AD_VECTOR_CATEGORY_LABELS,
  AD_VECTOR_CATEGORY_ORDER,
  AD_VECTOR_SHAPES,
  CUSTOM_VECTOR_CATEGORY_LABELS,
  groupAdVectorShapes,
  type AdElement,
  type AdVectorShapeDef,
  type AdVectorShapeId,
  type CustomVectorCategory,
} from '@goenhub/shared';
import { Image as ImageIcon, Loader2, Plus, Shapes, Trash2, Type, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import {
  useCustomVectors,
  useDeleteCustomVector,
  useUploadCustomVector,
  type CustomVectorRecord,
} from '@/hooks/useCustomVectors';

interface Props {
  /** 基準解像度 (preset.width, preset.height) */
  presetWidth: number;
  presetHeight: number;
  /** 既存要素の最大 zIndex（新規要素はこれ +1 で前面に置く） */
  maxZIndex: number;
  /** 要素を 1 つ追加する */
  onAdd: (element: AdElement) => void;
}

export function AddElementMenu({ presetWidth, presetHeight, maxZIndex, onAdd }: Props) {
  const [shapeDialogOpen, setShapeDialogOpen] = useState(false);
  const { data: customVectors = [] } = useCustomVectors();
  const upload = useUploadCustomVector();
  const remove = useDeleteCustomVector();

  // アップロードフォーム state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadCategory, setUploadCategory] = useState<CustomVectorCategory>('decoration');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const newId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // キャンバス中央
  const cx = presetWidth / 2;
  const cy = presetHeight / 2;

  const addText = () => {
    const w = Math.round(presetWidth * 0.5);
    const h = Math.round(presetHeight * 0.1);
    onAdd({
      id: newId(),
      kind: 'text',
      content: 'テキストを入力',
      x: Math.round(cx - w / 2),
      y: Math.round(cy - h / 2),
      w,
      h,
      zIndex: maxZIndex + 1,
      visible: true,
      rotation: 0,
      locked: false,
      fontFamily: 'Noto Sans JP',
      fontSize: Math.round(presetHeight * 0.05),
      fontWeight: 700,
      color: '#111111',
      align: 'center',
      lineHeight: 1.2,
      letterSpacing: 0,
      shadow: null,
      background: null,
      padding: 0,
    });
  };

  const addImagePlaceholder = () => {
    const w = Math.round(presetWidth * 0.4);
    const h = Math.round(presetHeight * 0.4);
    onAdd({
      id: newId(),
      kind: 'image_placeholder',
      label: '挿入画像',
      imageUrl: null,
      x: Math.round(cx - w / 2),
      y: Math.round(cy - h / 2),
      w,
      h,
      zIndex: maxZIndex + 1,
      visible: true,
      rotation: 0,
      locked: false,
      fit: 'cover',
      borderRadius: 0,
    });
  };

  const addVector = (shape: AdVectorShapeId, def: AdVectorShapeDef) => {
    const w = Math.round(presetWidth * 0.2);
    const h = Math.round(presetHeight * 0.2);
    // フレーム / 線画装飾は大きめ + 線色を使う
    const isLineart = def.strokeOnly === true;
    const isFrameOrStamp = def.category === 'frame' || def.category === 'stamp';
    const sizeMul = def.category === 'frame' ? 0.5 : 0.2;
    const fw = Math.round(presetWidth * sizeMul);
    const fh = Math.round(presetHeight * sizeMul);
    onAdd({
      id: newId(),
      kind: 'vector',
      shape,
      x: Math.round(cx - (isFrameOrStamp ? fw : w) / 2),
      y: Math.round(cy - (isFrameOrStamp ? fh : h) / 2),
      w: isFrameOrStamp ? fw : w,
      h: isFrameOrStamp ? fh : h,
      zIndex: maxZIndex + 1,
      visible: true,
      rotation: 0,
      locked: false,
      fill: isLineart ? 'none' : '#ff6b6b',
      stroke: isLineart ? '#111111' : 'none',
      strokeWidth: isLineart ? 2 : 0,
      opacity: 1,
    });
    setShapeDialogOpen(false);
  };

  const addCustomVector = (cv: CustomVectorRecord) => {
    const w = Math.round(presetWidth * 0.3);
    const h = Math.round(presetHeight * 0.3);
    onAdd({
      id: newId(),
      kind: 'vector',
      shape: 'rect', // 未使用（customSvgUrl が優先される）
      x: Math.round(cx - w / 2),
      y: Math.round(cy - h / 2),
      w,
      h,
      zIndex: maxZIndex + 1,
      visible: true,
      rotation: 0,
      locked: false,
      fill: 'none',
      stroke: 'none',
      strokeWidth: 0,
      opacity: 1,
      customSvgUrl: cv.url,
      customSvgLabel: cv.name,
    });
    setShapeDialogOpen(false);
  };

  const onPickUploadFile = (f: File | null) => {
    setUploadFile(f);
    if (f && !uploadName) setUploadName(f.name.replace(/\.[^.]+$/, ''));
  };

  const onUploadSubmit = async () => {
    if (!uploadFile) {
      toast.error('SVG ファイルを選択してください');
      return;
    }
    if (!uploadName.trim()) {
      toast.error('名前を入力してください');
      return;
    }
    try {
      await upload.mutateAsync({
        file: uploadFile,
        name: uploadName.trim(),
        category: uploadCategory,
      });
      toast.success('SVG をアップロードしました');
      setUploadFile(null);
      setUploadName('');
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error((e as Error).message ?? 'アップロードに失敗しました');
    }
  };

  const onDeleteCustom = async (cv: CustomVectorRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`「${cv.name}」を削除しますか？`)) return;
    try {
      await remove.mutateAsync(cv);
      toast.success('削除しました');
    } catch (err) {
      toast.error((err as Error).message ?? '削除に失敗しました');
    }
  };

  const grouped = groupAdVectorShapes();

  // カスタム SVG をカテゴリ別にグループ化
  const customByCat = customVectors.reduce<Record<CustomVectorCategory, CustomVectorRecord[]>>(
    (acc, cv) => {
      if (!acc[cv.category]) acc[cv.category] = [];
      acc[cv.category].push(cv);
      return acc;
    },
    { frame: [], stamp: [], decoration: [], other: [] },
  );

  return (
    <>
      <div className="flex items-center gap-1 p-2 border border-[var(--border)] rounded-sm bg-[var(--bg-elevated)]">
        <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mr-1">
          <Plus className="size-3 inline" /> 追加
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={addText} title="テキストを追加">
          <Type className="size-3 mr-1" /> テキスト
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={addImagePlaceholder} title="画像プレースホルダーを追加">
          <ImageIcon className="size-3 mr-1" /> 画像
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShapeDialogOpen(true)}
          title="ベクター shape を追加"
        >
          <Shapes className="size-3 mr-1" /> ベクター
        </Button>
      </div>

      <Dialog open={shapeDialogOpen} onOpenChange={setShapeDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>ベクター・装飾を選択</DialogTitle>
            <DialogDescription>
              基本図形・記号・フレーム・スタンプ・カスタム SVG から選択してキャンバスに追加します。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {/* 組み込みカテゴリ */}
            {AD_VECTOR_CATEGORY_ORDER.map((cat) => {
              const group = grouped[cat];
              if (!group.length) return null;
              return (
                <section key={cat}>
                  <h4 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
                    {AD_VECTOR_CATEGORY_LABELS[cat]}
                  </h4>
                  <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
                    {group.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => addVector(s.id, s)}
                        className="aspect-square rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 p-2 flex flex-col items-center justify-center gap-1 transition-colors"
                        title={s.label}
                      >
                        <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                          {s.strokeOnly ? (
                            <path d={s.d} fill="none" stroke="currentColor" strokeWidth={3} className="text-[var(--text-primary)]" />
                          ) : (
                            <path d={s.d} fill="currentColor" className="text-[var(--text-primary)]" />
                          )}
                        </svg>
                        <span className="text-[9px] text-[var(--text-muted)] truncate w-full text-center">
                          {s.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}

            {/* カスタム SVG（カテゴリ別） */}
            {customVectors.length > 0 && (
              <section className="border-t border-[var(--border)] pt-3">
                <h4 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
                  カスタム SVG
                </h4>
                {(['frame', 'stamp', 'decoration', 'other'] as CustomVectorCategory[]).map((cat) => {
                  const list = customByCat[cat];
                  if (!list || !list.length) return null;
                  return (
                    <div key={cat} className="mb-3">
                      <h5 className="text-[10px] text-[var(--text-muted)] mb-1">
                        {CUSTOM_VECTOR_CATEGORY_LABELS[cat]}
                      </h5>
                      <div className="grid grid-cols-5 gap-2 sm:grid-cols-10">
                        {list.map((cv) => (
                          <div key={cv.id} className="relative group">
                            <button
                              type="button"
                              onClick={() => addCustomVector(cv)}
                              className="w-full aspect-square rounded-sm border border-[var(--border)] bg-[var(--bg-elevated)] hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 p-2 flex flex-col items-center justify-center gap-1 transition-colors"
                              title={cv.name}
                            >
                              <img
                                src={cv.url}
                                alt={cv.name}
                                className="w-full h-full object-contain"
                                draggable={false}
                              />
                              <span className="text-[9px] text-[var(--text-muted)] truncate w-full text-center">
                                {cv.name}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => onDeleteCustom(cv, e)}
                              className="absolute top-0.5 right-0.5 bg-[var(--bg-surface)]/80 rounded-sm p-0.5 opacity-0 group-hover:opacity-100 hover:text-[var(--accent-red)]"
                              aria-label="delete"
                            >
                              <Trash2 className="size-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {/* SVG アップロードフォーム */}
            <section className="border-t border-[var(--border)] pt-3">
              <h4 className="text-[11px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
                SVG をアップロード
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <Input
                  ref={fileRef}
                  type="file"
                  accept=".svg,image/svg+xml"
                  onChange={(e) => onPickUploadFile(e.target.files?.[0] ?? null)}
                />
                <Input
                  placeholder="表示名"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                />
                <select
                  className="rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 py-2 text-[13px]"
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value as CustomVectorCategory)}
                >
                  {(['frame', 'stamp', 'decoration', 'other'] as CustomVectorCategory[]).map((c) => (
                    <option key={c} value={c}>
                      {CUSTOM_VECTOR_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                size="sm"
                className="mt-2"
                onClick={onUploadSubmit}
                disabled={upload.isPending || !uploadFile}
              >
                {upload.isPending ? (
                  <Loader2 className="size-3 mr-1 animate-spin" />
                ) : (
                  <Upload className="size-3 mr-1" />
                )}
                アップロード
              </Button>
              <p className="text-[10px] text-[var(--text-muted)] mt-1">
                .svg のみ / 最大 1MB / 配色は SVG ファイル側で固定（アップロード後は変更不可）
              </p>
            </section>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

void AD_VECTOR_SHAPES;
